// @ts-nocheck
import React, { useState, useRef, useCallback } from 'react';
import { useAppState, useDispatch } from '../store';
import * as api from '../api';

export default function ImportExcel({ onClose }: { onClose: () => void }) {
  const state = useAppState();
  const dispatch = useDispatch();
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [sheets, setSheets] = useState<string[]>([]);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<any[][]>([]);
  const [workbook, setWorkbook] = useState<any>(null);
  const [mapping, setMapping] = useState<Record<number, string>>({});
  const [targetGroupId, setTargetGroupId] = useState('');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState('');

  const columns = [...state.columns].sort((a, b) => a.position - b.position);
  const groups = [...state.groups].sort((a, b) => a.position - b.position);

  const processFile = useCallback(async (file: File) => {
    const XLSX = await import('xlsx');
    const data = await file.arrayBuffer();
    const wb = XLSX.read(data, { type: 'array' });
    setWorkbook(wb);
    setSheets(wb.SheetNames);
    if (wb.SheetNames.length > 0) {
      loadSheet(wb, wb.SheetNames[0], XLSX);
    }
  }, []);

  const loadSheet = (wb: any, sheetName: string, XLSX: any) => {
    setSelectedSheet(sheetName);
    const ws = wb.Sheets[sheetName];
    const json: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
    if (json.length > 0) {
      setHeaders(json[0].map(String));
      setRows(json.slice(1).filter((row) => row.some((c: any) => c !== '' && c !== null)));
    }
    // Auto-set target group
    if (groups.length > 0 && !targetGroupId) {
      setTargetGroupId(groups[0].id);
    }
  };

  const handleSheetChange = (name: string) => {
    import('xlsx').then((XLSX) => {
      loadSheet(workbook, name, XLSX);
    });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleImport = async () => {
    if (!state.activeBoardId || !targetGroupId) return;
    setImporting(true);

    const columnMapping = Object.entries(mapping)
      .filter(([, colId]) => colId)
      .map(([excelIdx, colId]) => ({ excel_col: Number(excelIdx), board_col_id: colId }));

    try {
      const res = await api.importExcel({
        board_id: state.activeBoardId,
        group_id: targetGroupId,
        column_mapping: columnMapping,
        rows,
      });
      setResult(`Successfully imported ${res.imported} items!`);

      // Reload board data
      const data = await api.getBoardData(state.activeBoardId);
      dispatch({
        type: 'SET_BOARD_DATA',
        groups: data.groups,
        columns: data.columns,
        items: data.items,
        cells: data.cells,
      });

      setTimeout(onClose, 1500);
    } catch (e: any) {
      setResult(`Error: ${e.message}`);
    }
    setImporting(false);
  };

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{ position: 'relative' }}>
        <button className="modal-close" onClick={onClose}>✕</button>
        <h2>Import from Excel</h2>

        {!headers.length ? (
          <div
            className={`import-dropzone ${dragging ? 'dragging' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}
          >
            <div className="import-dropzone-icon"><svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#676879" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg></div>
            <p><strong>Drop your Excel file here</strong></p>
            <p style={{ color: '#676879', fontSize: 13, marginTop: 4 }}>or click to browse</p>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
          </div>
        ) : (
          <>
            {sheets.length > 1 && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 13, color: '#676879', marginRight: 8 }}>Sheet:</label>
                <select
                  value={selectedSheet}
                  onChange={(e) => handleSheetChange(e.target.value)}
                  style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #e6e9ef' }}
                >
                  {sheets.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, color: '#676879', marginRight: 8 }}>Import to group:</label>
              <select
                value={targetGroupId}
                onChange={(e) => setTargetGroupId(e.target.value)}
                style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #e6e9ef' }}
              >
                {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>

            <div style={{ fontSize: 13, color: '#676879', marginBottom: 8 }}>
              Map Excel columns to board columns ({rows.length} rows to import):
            </div>

            <div className="import-preview" style={{ maxHeight: 400, overflow: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    {headers.map((h, i) => (
                      <th key={i}>
                        <div>{h}</div>
                        <select
                          value={mapping[i] || ''}
                          onChange={(e) => setMapping({ ...mapping, [i]: e.target.value })}
                          style={{ marginTop: 4 }}
                        >
                          <option value="">— Skip —</option>
                          <option value="__name__">Item Name</option>
                          {columns.map((col) => (
                            <option key={col.id} value={col.id}>{col.name} ({col.type})</option>
                          ))}
                        </select>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 5).map((row, ri) => (
                    <tr key={ri}>
                      {row.map((cell: any, ci: number) => (
                        <td key={ci}>{cell !== null && cell !== undefined ? String(cell) : ''}</td>
                      ))}
                    </tr>
                  ))}
                  {rows.length > 5 && (
                    <tr>
                      <td colSpan={headers.length} style={{ textAlign: 'center', color: '#676879' }}>
                        ...and {rows.length - 5} more rows
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {result && (
              <div style={{
                padding: '8px 12px', borderRadius: 6, marginTop: 12, fontSize: 13,
                background: result.startsWith('Error') ? '#fff0f0' : '#f0fff0',
                color: result.startsWith('Error') ? '#e2445c' : '#00c875',
              }}>
                {result}
              </div>
            )}

            <div className="import-actions">
              <button className="import-btn cancel" onClick={onClose}>Cancel</button>
              <button
                className="import-btn confirm"
                onClick={handleImport}
                disabled={importing || !targetGroupId}
              >
                {importing ? 'Importing...' : `Import ${rows.length} rows`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
